import { Polygon } from 'ol/geom';
import { Feature } from 'ol/index';

/**
 * Parses footprint data into OpenLayers features
 * @param {Object} params - The parameters object
 * @param {Object} params.data - The raw footprint data
 * @param {string} params.featureType - The type of feature to create
 * @returns {Array<Feature>} Array of OpenLayers features
 */
export const parseFootprintData = ({ data, featureType }) => {
	if (!data?.features?.length) return [];

	try {
		return data.features
			.filter(feature => feature?.geometry?.coordinates)
			.map(feature => {
				const { properties } = feature;
				return new Feature({
					geometry: new Polygon(feature.geometry.coordinates),
					area: properties.image_dimensions?.area_m2,
					lrfActive: properties.image_location?.lrf_active,
					featureType,
					hovering: 0,
				});
			});
	} catch (error) {
		console.error('Failed to parse footprint data:', error);
		return [];
	}
};

/**
 * Parses DSP data into OpenLayers features
 * @param {Object} params - The parameters object
 * @param {Object} params.data - The raw DSP data
 * @param {string} params.featureType - The type of feature to create
 * @param {Array} params.dspFeaturesInfo - Additional DSP feature information
 * @param {Array} params.pickedModelClasses - Selected model classes
 * @returns {Array<Feature>} Array of OpenLayers features
 */
export const parseDspData = ({
	data,
	featureType,
	dspFeaturesInfo,
	pickedModelClasses,
}) => {
	if (!data?.plot_data || !pickedModelClasses?.length) return [];

	try {
		return Object.entries(data.plot_data)
			.filter(([_, item]) => item?.metadata?.plot_polygon)
			.map(([key, item]) => {
				const lrfActive = dspFeaturesInfo.find(
					info => info.tile_name === item.metadata.filename
				)?.lrfActive;

				const detectionClasses = Object.entries(
					item.detections || {}
				).map(([className, values]) => ({
					id:
						pickedModelClasses.find(
							model => model.label === className
						)?.id || 0,
					count: values.count,
					hectare_equivalent: values.hectare_equivalent,
					className,
				}));

				return new Feature({
					geometry: new Polygon([item.metadata.plot_polygon]),
					detectionClasses,
					area: item.metadata.area,
					totalDetectsCount: item.metadata.count,
					lrfActive,
					featureType,
					hovering: 0,
				});
			});
	} catch (error) {
		console.error('Failed to parse DSP data:', error);
		return [];
	}
};

const getTooltipPosition = geometry => {
	if (!geometry) return undefined;
	const extent = geometry.getExtent();
	return [(extent[0] + extent[2]) / 2, extent[3]]; // top center
};

export const handleFootprintFeatureHover = ({
	feature,
	tooltip,
	mapObject,
	toolBarVisible,
	featureType,
	hoveredFeature,
	setTooltipContent,
}) => {
	if (!mapObject || !toolBarVisible) return;

	// This is not the way to do it, but I needed a quick fix
	const posibleFeatureTypes = ['digitalSamplePlot', 'footprint'];

	const currentFeatureType = feature?.get('featureType');
	const isTargetFeature = posibleFeatureTypes.includes(currentFeatureType);

	//const isTargetFeature = feature?.get('featureType') === featureType;

	// Reset previous hover state
	if (hoveredFeature.current) {
		hoveredFeature.current.set('hovering', 0);
	}

	if (isTargetFeature) {
		const position = getTooltipPosition(feature.getGeometry());
		if (!position) return;

		// Update UI state
		mapObject.getTargetElement().style.cursor = 'pointer';
		tooltip.setPosition(position);
		feature.set('hovering', 1);
		hoveredFeature.current = feature;

		// Update tooltip content
		setTooltipContent({
			detectionClasses: feature.get('detectionClasses') || [],
			area: feature.get('area'),
			lrfActive: feature.get('lrfActive') ?? null,
		});
	} else {
		// Reset all state when no feature is hovered
		mapObject.getTargetElement().style.cursor = '';
		tooltip.setPosition(undefined);
		hoveredFeature.current = null;
		setTooltipContent(null);
	}
};
